import { useId, useState } from 'react'
import { Package, Server, Tag } from 'lucide-react'

import { Badge } from '../../ui/Badge'
import { Button } from '../../ui/Button'
import { RENTAL_PATH_COPY, isExclusive, isHostProduct, type RentalMode, type RentalPathCopy } from '../../core/rental'

/**
 * What the tenant chose, restated where they are configuring it. Without this
 * the choice made on the chooser is invisible from here, and a tenant three
 * screens in has no way to check they are still buying what they picked.
 *
 * One row, not a notice: the guarantee is clamped to a line on wide screens,
 * never shortened. Its full text stays in the DOM (and in `title`), and
 * "Details" lifts the clamp. The wording itself belongs to `core/rental.ts`.
 */
export function RentalChoiceStrip({ rentalMode, openRequest, chosenPath, onNavigate }: { rentalMode: RentalMode; openRequest: boolean; chosenPath: RentalPathCopy; onNavigate?: ((path: string) => void) | undefined }) {
  const [expanded, setExpanded] = useState(false)
  const guaranteeId = useId()
  const competitive = rentalMode === 'RENTAL_MODE_COMPETITIVE'
  const title = competitive ? openRequest ? 'Request offers with no maximum' : RENTAL_PATH_COPY.competitive.title : chosenPath.title
  const guarantee = competitive ? openRequest ? 'You set no maximum. Offers can arrive at any price, and you choose before accepting one.' : RENTAL_PATH_COPY.competitive.guarantee : chosenPath.guarantee
  const Icon = isHostProduct(rentalMode) ? Server : competitive ? Tag : Package
  return <div className={`rental-chosen rental-chosen--strip${expanded ? ' rental-chosen--expanded' : ''}`} role="status" data-mode={rentalMode}>
    <Icon aria-hidden="true" size={16} className="rental-chosen-icon" />
    <strong>{title}</strong>
    {isHostProduct(rentalMode) ? (
      <Badge tone={isExclusive(rentalMode) ? 'positive' : 'neutral'}>
        {isExclusive(rentalMode) ? 'Exclusive machine' : 'Shared machine'}
      </Badge>
    ) : null}
    <span className="rental-chosen-guarantee" id={guaranteeId} title={guarantee}>{guarantee}</span>
    <Button type="button" variant="ghost" size="sm" className="rental-chosen-details" aria-expanded={expanded} aria-controls={guaranteeId} onClick={() => setExpanded((open) => !open)}>Details</Button>
    <Button type="button" variant="ghost" size="sm" aria-label="Change rental type" onClick={() => onNavigate?.('/app/rent')}>Change</Button>
  </div>
}
